import { axiosClient } from '@App/api/api-client'
import Pathes from '@App/dicts/pathes'
import { RightsholderClaimedShareList, Rightsholders, TipaltiIframe } from '@Entities/rightsholder'
import { AxiosResponse } from 'axios'
import { PaginationBody, PaginationBodyRightsHolders } from '@Entities/pagination'
import { Payees } from '@Entities/rightsholder/payee'
import { Questions } from '@Entities/questionnaire/question'
import CreateRightsholderDto from '@Entities/rightsholder/create-rightsholder-dto'
import { MemberSummary } from '@Entities/member/member-summary'
import { ResultPage } from '@Entities/result-page'
import { MemberActionDiagnostic } from '@Entities/member/member-action-diagnostic'
import { Collection } from '@Entities/collection'
import {
    WorkMergeRequest,
    WorkMergeRequestDetail,
    WorkMergeRequestSummary,
    WorkMergeVoteUpdate,
} from '@Entities/rightsholder/work-merge'

export const getRightsholders = (params: PaginationBodyRightsHolders): Promise<AxiosResponse<Rightsholders>> =>
    axiosClient.get<Rightsholders>(Pathes.RightsHolder.rightsHolders, { params: params })

export const searchRightsholders = (
    params: PaginationBody,
    nameFilter: string | undefined
): Promise<AxiosResponse<ResultPage<MemberSummary>>> =>
    axiosClient.get<ResultPage<MemberSummary>>(Pathes.RightsHolder.memberList, {
        params: { page: params.page ? params.page - 1 : undefined, size: params.limit, name: nameFilter },
    })

export const getClaimedShares = (
    rightsholderId: number,
    searchWorkIds: number[]
): Promise<AxiosResponse<RightsholderClaimedShareList>> =>
    axiosClient.post<RightsholderClaimedShareList>(Pathes.RightsHolder.claimedShares(rightsholderId), {
        workIds: searchWorkIds,
    })

export const getRightsHolderTipaltiIframe = (
    rightsHolderId: number,
    payeeIpId: number
): Promise<AxiosResponse<TipaltiIframe>> =>
    axiosClient.get<TipaltiIframe>(Pathes.RightsHolder.tipaltiIframeUrl(rightsHolderId, payeeIpId))

export const getRightsholderSummary = (rightsHolderId: number): Promise<AxiosResponse<MemberSummary>> =>
    axiosClient.get<MemberSummary>(Pathes.RightsHolder.rightsHolderSummary(rightsHolderId))

export const submitCreateRightsholder = (data: Partial<CreateRightsholderDto>): Promise<AxiosResponse> =>
    axiosClient.post<any>(Pathes.RightsHolder.createRightsHolder, data)

export const getRelatedRightsholders = (): Promise<AxiosResponse<Array<{ name: string }>>> =>
    axiosClient.get<Array<{ name: string }>>(Pathes.RightsHolder.getRelatedRightsholders())

export const verifyRightsholder = (hfaPublisherNumber: string): Promise<AxiosResponse<boolean>> =>
    axiosClient.get<boolean>(Pathes.RightsHolder.verifyRightsholder(hfaPublisherNumber))

export const editRightsholder = (rightsHolderId: number, data: Partial<MemberSummary>): Promise<AxiosResponse<void>> =>
    axiosClient.post<void>(Pathes.RightsHolder.editRightsHolder(rightsHolderId), data)

export const getAllPayees = (rightsholderId: number): Promise<AxiosResponse<Payees>> =>
    axiosClient.get<Payees>(Pathes.RightsHolder.allPayees(rightsholderId))

export const getNewRightsholderQuestions = (): Promise<AxiosResponse<Questions>> =>
    axiosClient.get<Questions>(Pathes.RightsHolder.newRightsholderQuestionnaire)

export const finalizeDocumentUpload = (
    rightsholderId: number,
    memberUploadId: number
): Promise<AxiosResponse<Payees>> =>
    axiosClient.put<Payees>(Pathes.RightsHolder.finalizeDocumentUpload(rightsholderId, memberUploadId))

export const createWorkMergeRequest = (
    rightsholderId: number,
    requests: WorkMergeRequest[]
): Promise<AxiosResponse<void>> => axiosClient.post<void>(Pathes.RightsHolder.workMerge(rightsholderId), requests)

export const getWorkMergeRequests = (
    rightsholderId: number
): Promise<AxiosResponse<Collection<WorkMergeRequestSummary>>> =>
    axiosClient.get<Collection<WorkMergeRequestSummary>>(Pathes.RightsHolder.workMerge(rightsholderId))

export const getWorkMergeReviewRequests = (
    rightsholderId: number
): Promise<AxiosResponse<Collection<WorkMergeRequestSummary>>> =>
    axiosClient.get<Collection<WorkMergeRequestSummary>>(Pathes.RightsHolder.workMergeReview(rightsholderId))

export const getWorkMergeRequest = (
    rightsholderId: number,
    workMergeRequestId: number
): Promise<AxiosResponse<WorkMergeRequestDetail>> =>
    axiosClient.get<WorkMergeRequestDetail>(Pathes.RightsHolder.workMergeRequest(rightsholderId, workMergeRequestId))

export const getWorkMergeReviewRequest = (
    rightsholderId: number,
    workMergeRequestId: number
): Promise<AxiosResponse<WorkMergeRequestDetail>> =>
    axiosClient.get<WorkMergeRequestDetail>(
        Pathes.RightsHolder.workMergeReviewRequest(rightsholderId, workMergeRequestId)
    )

export const updateWorkMergeVote = (
    rightsholderId: number,
    workMergeRequestId: number,
    workId: number,
    voteUpdate: WorkMergeVoteUpdate
): Promise<AxiosResponse<void>> =>
    axiosClient.patch<void>(
        Pathes.RightsHolder.workMergeRequestWork(rightsholderId, workMergeRequestId, workId),
        voteUpdate
    )

export const getMemberActionDiagnostic = (rightsholderId: number): Promise<AxiosResponse<MemberActionDiagnostic>> =>
    axiosClient.get<MemberActionDiagnostic>(Pathes.RightsHolder.diagnosticDashboard(rightsholderId))
